// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { classNames, numberToFixed } from '@shared/ui-kit/utils';
import { createColumnHelper } from '@tanstack/react-table';
import { type Returns } from '@shared/data-access/interfaces/api/ai/screener';
import { AssetBrand } from '@shared/ui-kit/components/popups';
import { type ColumnMethods } from '@advisor-app/features/trades';
import { formatToMoney } from '@advisor-app/features/utils/format-to-money';
import { type TradesOperations } from '@shared/data-access/interfaces/redux/trades';
import { CellData, WatchlistToggleButton } from '@shared/ui-kit/components';
import { DeleteButton } from '@shared/ui-kit/components/delete-button';
import type { TFunction } from 'i18next';

export interface InterfacePositionsAnalysisData extends Returns {
  id?: number;
  ticker: string;
  isin: string;
  name: string;
  outstanding_balance: number;
  unrealized_capital_gains: [number, number];
  quantity: number;
  price: number;
  weight: number;
  url_logo: string;
  url_morningstar: string;
  asset_type?: string;
  operation: TradesOperations;
  volume: number;
  asset_class: string;
}

const columnHelper = createColumnHelper<InterfacePositionsAnalysisData>();

export const renderColumnsAnalysis = (
  columnMethods: ColumnMethods,
  t: TFunction,
) => {
  const { handleDeleteTrade, assetType, hasOnlyOneData } = columnMethods;

  const isStock = assetType === 'stock';

  const columns = [
    columnHelper.accessor('name', {
      cell: ({ row }) => (
        <AssetBrand
          ticker={row.original.ticker}
          assetType={row.original.asset_type}
          label={row.original.name}
          url={row.original.url_morningstar}
          logoUrl={row.original.url_logo}
          watchlistButton={
            <WatchlistToggleButton
              ticker={row.original.ticker}
              assetType={row.original.asset_type!}
            />
          }
        />
      ),
      minSize: 250,
      header: t('common.name'),
      enableHiding: false,
    }),

    columnHelper.accessor('ticker', {
      cell: ({ getValue }) => getValue(),
      header: t('common.ticker'),
      enableHiding: false,
    }),

    columnHelper.accessor('isin', {
      header: t('common.isin'),
      cell: ({ getValue }) => getValue() ?? '-',
    }),

    columnHelper.accessor('asset_class', {
      header: t('common.asset-class'),
      meta: { initiallyHidden: isStock },
      cell: ({ getValue }) => getValue() ?? '-',
    }),

    columnHelper.accessor('weight', {
      header: t('common.weight'),
      meta: { alignment: 'end' },
      cell: ({ getValue }) => (
        <span className="!money-display">
          {numberToFixed(getValue(), 1) + ' %'}
        </span>
      ),
    }),

    columnHelper.accessor('outstanding_balance', {
      header: t('common.investment'),
      meta: { alignment: 'end' },
      cell: ({ getValue }) => (
        <span className="!money-display">{formatToMoney(getValue())}</span>
      ),
    }),

    columnHelper.accessor('unrealized_capital_gains', {
      header: t('common.gain-loss'),
      minSize: 150,
      sortingFn: 'gainLoss',
      meta: { alignment: 'end' },
      cell: ({ getValue }) => {
        const [value, percentage] = getValue();
        return (
          <div className={value >= 0 ? 'profit' : 'loss'}>
            {formatToMoney(value)} ({percentage}%)
          </div>
        );
      },
    }),

    columnHelper.accessor('ytd_return', {
      header: t('common.ytd-returns'),
      meta: { initiallyHidden: isStock, alignment: 'end' },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('y1_return', {
      header: t('common.investment-returns-1y'),
      meta: { initiallyHidden: isStock, alignment: 'end' },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('y3_return', {
      header: t('common.investment-returns-3y'),
      meta: { initiallyHidden: isStock, alignment: 'end' },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('y5_return', {
      header: t('common.investment-returns-5y'),
      meta: { initiallyHidden: isStock, alignment: 'end' },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('y10_return', {
      header: t('common.investment-returns-10y'),
      meta: { initiallyHidden: isStock, alignment: 'end' },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('y3_vol', {
      header: t('common.3y-vol'),
      meta: { initiallyHidden: isStock, alignment: 'end' },
      cell: ({ getValue }) => (
        <CellData
          value={getValue()}
          format="percentage"
          decimalPlaces={1}
          emptyValue={-1}
        />
      ),
    }),

    columnHelper.accessor('max_draw_down', {
      header: t('common.max-drawdown'),
      meta: { initiallyHidden: isStock, alignment: 'end' },
      cell: ({ getValue }) => (
        <CellData
          value={getValue()}
          format="percentage"
          decimalPlaces={1}
          emptyValue={-1}
        />
      ),
    }),

    columnHelper.accessor('inception_return', {
      header: t('common.investment-returns-inception'),
      meta: {
        initiallyHidden: true,
        alignment: 'end',
      },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('m1_return', {
      header: t('common.investment-returns-m1'),
      meta: {
        initiallyHidden: true,
        alignment: 'end',
      },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('m3_return', {
      header: t('common.investment-returns-m3'),
      meta: {
        initiallyHidden: true,
        alignment: 'end',
      },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('m6_return', {
      header: t('common.investment-returns-m6'),
      meta: {
        initiallyHidden: true,
        alignment: 'end',
      },
      cell: ({ getValue }) => <CellData value={getValue()} format="performance" />,
    }),

    columnHelper.accessor('price', {
      header: t('common.price'),
      meta: {
        initiallyHidden: true,
        alignment: 'end',
      },
      cell: ({ getValue }) => <CellData value={getValue()} format="currency" />,
    }),

    columnHelper.accessor('price_as_of', {
      header: t('common.updated-on'),
      meta: {
        initiallyHidden: true,
      },
      cell: ({ getValue }) => <CellData value={getValue()} format="date" />,
    }),

    columnHelper.display({
      id: 'options',
      meta: { showColumnVisibilitySelector: true },
      cell: ({ row }) => (
        <DeleteButton
          className={classNames({ '!hidden': hasOnlyOneData })}
          onClick={() => handleDeleteTrade?.(row.original.ticker)}
          toolTipTitle={t('common.delete')}
          data-testid="delete-trade"
        />
      ),
    }),
  ];

  return columns;
};
